import { DialogTitle } from "@headlessui/react";
import { Elements } from "@stripe/react-stripe-js";
import { Tag } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCheckoutSession } from "./CheckoutSession";
import { FiClock } from "react-icons/fi";
import { useFetcher, useNavigate, useRevalidator, useSearchParams } from "react-router";
import {
  CreateEventTicketPurchaseIntentErrors,
  type EventFragment,
  type EventTicketApprovalRequestInput,
} from "~/__generated__/graphql/graphql";
import { PrimaryButton } from "~/components/Button";
import { StepIndicator } from "~/components/plans/components/StepIndicator";
import { Text } from "~/components/Text";
import type { PurchaseIntentResult } from "~/routes/api.events.$eventId.purchase-intent";
import type { TicketQuoteResult } from "~/routes/api.events.$eventId.ticket-quote";
import { useRootLocale } from "~/root";
import { getRegionalCopy } from "~/utils/localization";
import { track } from "~/utils/analytics";
import { centsToMoney } from "~/utils/money";
import { getStripePromise } from "~/utils/stripe";
import { CheckoutSummaryCard } from "./CheckoutSummaryCard";
import { CheckoutFooter } from "./CheckoutFooter";

import { PurchaseForm } from "./PurchaseForm";
import {
  getSelectedTicketVariation,
  TICKET_VARIATION_PARAM,
} from "./ticketVariations";

const STRIPE_ELEMENTS_APPEARANCE = {
  variables: {
    colorPrimary: "#F3564F",
    colorBackground: "#FFFFFF",
    colorText: "#4A5043",
    colorTextPlaceholder: "#C7C7CD",
    borderRadius: "9px",
    fontFamily: "Proxima-Nova, system-ui, sans-serif",
  },
  rules: {
    ".Label": {
      color: "#4A5043",
      fontFamily: "Proxima-Nova",
      fontWeight: "700",
      fontSize: "13.5px",
    },
    ".Tab": {
      borderColor: "#F5DCC4",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      color: "#4A5043",
    },
    ".Tab--selected": {
      borderColor: "#F5DCC4",
      backgroundColor: "rgba(245, 220, 196, 0.8)",
      color: "#4A5043",
    },
    ".Input": {
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "#F5DCC4",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      color: "#4A5043",
      fontFamily: "Proxima-Nova",
    },
    ".Input:focus": {
      borderColor: "#4A5043",
      boxShadow: "none",
    },
    ".Input--invalid": {
      borderColor: "#df1b41",
    },
  },
} as const;

const STRIPE_ELEMENTS_FONTS = [
  {
    family: "Proxima-Nova",
    src: "url(/Proxima-Nova-Regular.otf)",
    weight: "400",
  },
] as const;

type Props = {
  plan: EventFragment;
  authenticated: boolean;
  userExists: boolean;
  stripePublishableKey: string | undefined;
  ticketVariationsEnabled: boolean;
  approvalRequest?: EventTicketApprovalRequestInput;
  onBack?: () => void;
};



export function CheckoutPaymentStep({
  plan,
  authenticated,
  userExists,
  stripePublishableKey,
  ticketVariationsEnabled,
  approvalRequest,
  onBack,
}: Props) {
  const locale = useRootLocale();
  const regionalCopy = getRegionalCopy(locale);
  const eventId = plan.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const revalidator = useRevalidator();
  const checkout = useCheckoutSession();
  const navigate = useNavigate();

  const { update: updateCheckout, flush: flushCheckout, canContinue } = checkout;
  const setPhase = useCallback((paymentPhase: "overview" | "method") => updateCheckout({ paymentPhase }), [updateCheckout]);
  const discountCode = searchParams.get("discountCode") ?? "";
  const requestedVariationID = searchParams.get(TICKET_VARIATION_PARAM);
  const selectedVariation =
    ticketVariationsEnabled && requestedVariationID
      ? getSelectedTicketVariation(plan, requestedVariationID)
      : undefined;
  const ticketVariationID = selectedVariation?.id;
  const approvalCheckout = approvalRequest !== undefined;
  const acceptedWaitlistClaim = !!approvalRequest?.eventRequestID;
  const hostFirstName = plan.hosts.find(host => host.primary)?.user.firstName?.trim() || "the host";
  const approvalPaymentNotice = `Your payment will be processed once ${hostFirstName} accepts.`;
  const serializedApprovalRequest = useMemo(
    () => (approvalRequest ? JSON.stringify(approvalRequest) : ""),
    [approvalRequest],
  );
  const recoveredIntentErrorRef = useRef<string | null>(null);
  const intentSubmissionRef = useRef<string | null>(null);
  const [preparationError, setPreparationError] = useState<string | null>(null);
  const intentKey = `checkout-intent:${checkout.scope}:${eventId}:${ticketVariationID ?? "full-access"}:${discountCode}:${serializedApprovalRequest}`;
  const currentIntentKey = useRef(intentKey);
  currentIntentKey.current = intentKey;

  const quoteFetcher = useFetcher<TicketQuoteResult>({
    key: `ticket-quote:${eventId}`,
  });
  const intentFetcher = useFetcher<PurchaseIntentResult>({
    key: intentKey,
  });
  const stripePromise = useMemo(
    () =>
      stripePublishableKey ? getStripePromise(stripePublishableKey) : null,
    [stripePublishableKey],
  );

  const setStep = (step: string) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("step", step);
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
  };

  const goToTickets = () => setStep("tickets");
  const goToDetails = () => {
    if (acceptedWaitlistClaim) {
      goToTickets();
      return;
    }
    setStep(approvalCheckout ? "questions" : "details");
  };

  useEffect(() => {
    if (
      ticketVariationsEnabled &&
      (!requestedVariationID || !ticketVariationID)
    ) {
      goToTickets();
      return;
    }
    if (!authenticated || !userExists) {
      setStep("details");
    }
    // `setStep` only updates checkout URL state and is intentionally local.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authenticated,
    requestedVariationID,
    ticketVariationID,
    ticketVariationsEnabled,
    userExists,
  ]);

  const loadQuote = quoteFetcher.load;
  useEffect(() => {
    if (ticketVariationsEnabled && !ticketVariationID) return;

    const params = new URLSearchParams();
    if (ticketVariationID) {
      params.set(TICKET_VARIATION_PARAM, ticketVariationID);
    }
    if (discountCode) params.set("discountCode", discountCode);
    loadQuote(`/api/events/${eventId}/ticket-quote?${params}`);
  }, [
    discountCode,
    eventId,
    loadQuote,
    ticketVariationID,
    ticketVariationsEnabled,
  ]);

  const quoteData = quoteFetcher.data;
  const quoteMatchesSelection =
    quoteData !== undefined &&
    quoteData.ticketVariationID === (ticketVariationID ?? null) &&
    quoteData.discountCode === discountCode;
  const quote = quoteMatchesSelection && quoteData.ok ? quoteData.ticketCost : null;
  const quoteError =
    quoteFetcher.state === "idle" && quoteMatchesSelection
      ? quoteData.error
      : null;

  const intentData = intentFetcher.data;
  const clientSecret = intentData?.clientSecret ?? null;
  const walletIntentID = intentData?.walletIntentID ?? null;
  const intentCost = intentData?.ticketCost;
  const currentCost = intentCost ?? quote;
  const totalCents =
    intentCost?.totalCostCents ??
    quote?.totalCostCents ??
    selectedVariation?.totalCostCents ??
    plan.regularTicketCost?.totalCostCents ??
    null;
  const originalTotalCents =
    intentCost?.originalTotalCents ?? quote?.originalTotalCents ?? null;
  const discountAmountCents =
    intentCost?.totalDiscountCents ?? quote?.totalDiscountCents ?? 0;

  useEffect(() => {
    if (!clientSecret) return;
    track("checkout_payment_viewed", {
      event_id: eventId,
      event_title: plan.title,
    });
  }, [clientSecret, eventId, plan.title]);


  useEffect(() => {
    const errorType = intentData?.errorType;
    if (intentData?.ok === false) {
      setPreparationError(intentData.error ?? "We couldn't prepare payment. Please try again.");
    }
    if (errorType === CreateEventTicketPurchaseIntentErrors.ProfileIncomplete) {
      intentSubmissionRef.current = null;
      if (recoveredIntentErrorRef.current === errorType) return;
      recoveredIntentErrorRef.current = errorType;
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set("step", "profile");
          return next;
        },
        { replace: true, preventScrollReset: true },
      );
      revalidator.revalidate();
      return;
    }
    if (errorType === "PRICE_CHANGED") {
      intentSubmissionRef.current = null;
      setPhase("overview");
      const params = new URLSearchParams();
      if (ticketVariationID) params.set(TICKET_VARIATION_PARAM, ticketVariationID);
      if (discountCode) params.set("discountCode", discountCode);
      if (recoveredIntentErrorRef.current !== errorType) {
        recoveredIntentErrorRef.current = errorType;
        loadQuote(`/api/events/${eventId}/ticket-quote?${params}`);
      }
      return;
    }
    const invalidDiscount = errorType === "DISCOUNT_CODE_INVALID";
    const invalidQuote = errorType === "TICKET_QUOTE_INVALID";
    const unavailableVariation =
      errorType ===
      CreateEventTicketPurchaseIntentErrors.TicketVariationUnavailable;
    const fullEvent =
      errorType === CreateEventTicketPurchaseIntentErrors.EventFull;

    if (
      !invalidDiscount &&
      !invalidQuote &&
      !unavailableVariation &&
      !fullEvent
    ) {
      if (intentData?.ok === false) {
        intentSubmissionRef.current = null;
        setPhase("overview");
      }
      return;
    }

    intentSubmissionRef.current = null;
    const recoveryKey = `${errorType}:${ticketVariationID ?? "full-access"}:${discountCode}`;
    if (recoveredIntentErrorRef.current === recoveryKey) return;
    recoveredIntentErrorRef.current = recoveryKey;

    if (invalidDiscount || invalidQuote) {
      setPhase("overview");
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.delete("discountCode");
          return next;
        },
        { replace: true, preventScrollReset: true },
      );
      return;
    }

    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("step", "tickets");
        next.delete(TICKET_VARIATION_PARAM);
        return next;
      },
      { replace: true, preventScrollReset: true },
    );
    revalidator.revalidate();
  }, [
    discountCode,
    intentData?.errorType,
    intentData?.error,
    intentData?.ok,
    revalidator,
    setSearchParams,
    ticketVariationID,
    eventId,
    loadQuote,
    setPhase,
  ]);

  useEffect(() => {
    if (!intentData?.ok || !walletIntentID) return;
    updateCheckout({ walletIntentID });
    if (!intentData.processing && (clientSecret || intentCost?.totalCostCents !== 0)) return;
    // Committed free or already-processing attempts reopen at the recovery
    // screen, never an editable form that could pay again.
    updateCheckout({ paymentPhase: "processing" });

    const processingURL = new URL(
      `/plans/${eventId}/purchase-processing`,
      window.location.origin,
    );
    processingURL.searchParams.set("walletIntentID", walletIntentID);
    let active = true;
    // The attempt ID is already in the destination URL. Draft storage must not strand
    // a committed free purchase or an in-flight payment on this screen.
    void flushCheckout().then(() => {
      if (active && canContinue()) navigate(processingURL.pathname + processingURL.search, { replace: true });
    });
    return () => { active = false; };
  }, [
    clientSecret,
    eventId,
    intentCost?.totalCostCents,
    intentData?.ok,
    intentData?.processing,
    walletIntentID,
    updateCheckout,
    flushCheckout,
    canContinue,
    navigate,
  ]);


  const createIntent = async () => {
    if (!quote?.available || intentFetcher.state !== "idle") return;
    if (intentData?.ok) {
      if (clientSecret) setPhase("method");
      return;
    }
    if (intentSubmissionRef.current === intentKey) return;
    intentSubmissionRef.current = intentKey;
    recoveredIntentErrorRef.current = null;
    setPreparationError(null);
    const flushed = await flushCheckout();
    if (currentIntentKey.current !== intentKey) return;
    if (!flushed || !canContinue()) {
      intentSubmissionRef.current = null;
      return;
    }

    if (quote.totalCostCents > 0) setPhase("method");
    const formData = new FormData();
    formData.append("expectedTotalCents", String(quote.totalCostCents));
    if (discountCode) formData.append("discountCode", discountCode);
    if (ticketVariationID) {
      formData.append(TICKET_VARIATION_PARAM, ticketVariationID);
    }
    if (serializedApprovalRequest) {
      formData.append("approvalRequest", serializedApprovalRequest);
    }
    intentFetcher.submit(formData, {
      method: "post",
      action: `/api/events/${eventId}/purchase-intent`,
    });
  };

  const quoteIsLoading =
    quoteFetcher.state !== "idle" ||
    !quoteMatchesSelection ||
    (!quote && !quoteError);
  const intentIsLoading = intentFetcher.state !== "idle";
  const paymentConfigurationError =
    totalCents !== null &&
    totalCents > 0 &&
    !stripePromise
      ? "Payment is not configured. Please try again later."
      : null;
  const displayedError = paymentConfigurationError ?? (intentIsLoading ? null
    : preparationError ?? intentData?.error ?? (!intentData?.ok ? quoteError : null));
  const showReview = totalCents === 0 || !!displayedError;

  useEffect(() => {
    // Preparing a paid attempt does not confirm it. A free attempt, however,
    // immediately gets the ticket or submits the request, so it needs a click.
    if (authenticated && userExists &&
      (!ticketVariationsEnabled || ticketVariationID) && stripePromise &&
      !intentData && !preparationError && quote?.available &&
      quote.totalCostCents > 0 && !quoteIsLoading) void createIntent();
    // Submission is deduplicated per selection, including StrictMode effect replays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, userExists, ticketVariationsEnabled,
    ticketVariationID, stripePromise, intentData, preparationError, quote, quoteIsLoading]);

  const paymentSummary = (
    <>
      <CheckoutSummaryCard
        plan={plan}
        ticketVariation={selectedVariation}
        totalCostCents={totalCents}
        originalTotalCents={originalTotalCents}
        discountAmountCents={discountAmountCents}
        discountCode={discountCode || undefined}
        onEditTickets={goToTickets}
      >
        {currentCost && (
          <PriceBreakdown
            basePriceCents={currentCost.basePriceCents}
            feeCents={currentCost.feeAmountCents + currentCost.stripeFeeCents}
            totalCents={currentCost.totalCostCents}
            discountAmountCents={currentCost.totalDiscountCents}
            originalTotalCents={currentCost.originalTotalCents}
            locale={locale}
          />
        )}
      </CheckoutSummaryCard>
      <Text variant="primary" size="md" as="h2" className="mt-5">
        Payment
      </Text>
      {approvalCheckout && (
        <Text as="p" variant="secondary" size="sm" className="mt-4">
          {approvalPaymentNotice}
        </Text>
      )}
    </>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col sm:block">
      <DialogTitle as="div" className="sr-only">
        {totalCents === 0 ? `Your ${regionalCopy.place}` : "Payment"}
      </DialogTitle>
      <div className="shrink-0">
        <StepIndicator
          currentStep={3}
          showTicketStep
          stepActions={{ 1: goToTickets, 2: goToDetails }}
        />
      </div>

      {showReview ? (
        <div className="mt-[10px] flex min-h-0 flex-1 flex-col sm:block">
          <div className="-mx-6 min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-[30px] pb-2 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0">
            <Text variant="primary" size="md" as="h2" className="mb-4">
              {`Your ${regionalCopy.place}`}
            </Text>

            <CheckoutSummaryCard
              plan={plan}
              ticketVariation={selectedVariation}
              totalCostCents={totalCents}
              originalTotalCents={originalTotalCents}
              discountAmountCents={discountAmountCents}
              discountCode={discountCode || undefined}
              onEditTickets={goToTickets}
            />

            <div className="mt-5 rounded-xl border border-butter-cream bg-white/70 p-4">
              {quote ? (
                <PriceBreakdown
                  basePriceCents={quote.basePriceCents}
                  feeCents={quote.feeAmountCents + quote.stripeFeeCents}
                  totalCents={quote.totalCostCents}
                  discountAmountCents={quote.totalDiscountCents}
                  originalTotalCents={quote.originalTotalCents}
                  locale={locale}
                />
              ) : quoteIsLoading ? (
                <PriceBreakdownSkeleton />
              ) : null}
            </div>

            {approvalCheckout && (
              <div className="mt-5 flex items-start gap-3 rounded-xl bg-butter-cream/55 p-4">
                <FiClock
                  size={18}
                  strokeWidth={2.5}
                  className="mt-0.5 shrink-0 text-butter-green"
                  aria-hidden
                />
                <Text as="p" variant="secondary" size="sm">
                  {totalCents === 0
                    ? "The host will review your request before your ticket is confirmed."
                    : approvalPaymentNotice}
                </Text>
              </div>
            )}
          </div>
          <CheckoutFooter onBack={onBack ?? goToDetails} backDisabled={intentIsLoading} error={displayedError}>

            <PrimaryButton
              type="button"
              className="text-base!"
              loading={intentIsLoading}
              disabled={!quote?.available || quoteIsLoading || intentIsLoading || !!paymentConfigurationError}
              onClick={createIntent}
            >
              {quote?.totalCostCents === 0
                ? approvalCheckout
                  ? "Send request"
                  : "Get ticket"
                : "Try again"}
            </PrimaryButton>

          </CheckoutFooter>
        </div>
      ) : (
        <div className="mt-[10px] flex min-h-0 flex-1 flex-col sm:block">
          {clientSecret && stripePromise && !intentData?.processing ? (
            <Elements
              key={clientSecret}
              stripe={stripePromise}
              options={{
                clientSecret,
                fonts: [...STRIPE_ELEMENTS_FONTS],
                appearance: STRIPE_ELEMENTS_APPEARANCE,
              }}
            >
              <PurchaseForm
                eventId={eventId}
                eventTitle={plan.title}
                clientSecret={clientSecret}
                totalCents={totalCents ?? 0}
                walletIntentID={walletIntentID}

                onBack={onBack ?? goToDetails}
                error={displayedError}
              >
                {paymentSummary}
              </PurchaseForm>
            </Elements>
          ) : (
            <>
              <div className="-mx-6 min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pt-[30px] pb-2 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0">
                {paymentSummary}
                {(intentIsLoading || !intentData) && (
                  <div className="mt-5"><PaymentFormSkeleton /></div>
                )}
              </div>
              <CheckoutFooter onBack={onBack ?? goToDetails} backDisabled>
                <PrimaryButton type="button" className="text-base!" disabled loading>
                  {intentData?.processing ? "Checking your ticket" : "Preparing payment"}
                </PrimaryButton>
              </CheckoutFooter>
            </>
          )}
        </div>
      )}
    </div>
  );
}

type PriceBreakdownProps = {
  basePriceCents: number;
  feeCents: number;
  totalCents: number;
  discountAmountCents: number;
  originalTotalCents: number | null;
  locale: string;
};

function PriceBreakdown({
  basePriceCents,
  feeCents,
  totalCents,
  discountAmountCents,
  originalTotalCents,
  locale,
}: PriceBreakdownProps) {
  const hasDiscount = discountAmountCents > 0;

  return (
    <div className="flex flex-col gap-2">
      <PriceRow label="Ticket" amountCents={basePriceCents} locale={locale} />
      <PriceRow label="Butter fee" amountCents={feeCents} locale={locale} />
      {hasDiscount && (
        <PriceRow
          label={
            <span className="inline-flex items-center gap-1.5">
              Discount
              <Tag className="size-3.5" aria-hidden />
            </span>
          }
          amountCents={discountAmountCents}
          locale={locale}
          negative
        />
      )}
      <div className="mt-1 flex items-center justify-between font-sans text-sm font-bold text-butter-green">
        <span>Total</span>
        <span className="inline-flex items-center gap-2">
          {hasDiscount && originalTotalCents !== null && (
            <span className="font-normal line-through opacity-60">
              {centsToMoney(originalTotalCents, "$", locale)}
            </span>
          )}
          <span>{centsToMoney(totalCents, "$", locale)}</span>
        </span>
      </div>
      {hasDiscount && (
        <p className="text-right font-sans text-sm font-normal text-butter-lime">
          You&apos;re saving {centsToMoney(discountAmountCents, "$", locale)}
        </p>
      )}
    </div>
  );
}

type PriceRowProps = {
  label: React.ReactNode;
  amountCents: number;
  locale: string;
  negative?: boolean;
};

function PriceRow({
  label,
  amountCents,
  locale,
  negative = false,
}: PriceRowProps) {
  return (
    <div className="flex items-center justify-between font-sans text-sm font-normal text-butter-green">
      <span>{label}</span>
      <span>
        {negative && "-"}
        {centsToMoney(amountCents, "$", locale)}
      </span>
    </div>
  );
}

function PriceBreakdownSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-label="Loading ticket price">
      <div className="flex h-6 items-center justify-between">
        <span className="h-4 w-16 animate-pulse rounded bg-butter-cream/70" />
        <span className="h-4 w-14 animate-pulse rounded bg-butter-cream/70" />
      </div>
      <div className="flex h-6 items-center justify-between">
        <span className="h-4 w-24 animate-pulse rounded bg-butter-cream/70" />
        <span className="h-4 w-12 animate-pulse rounded bg-butter-cream/70" />
      </div>
      <div className="flex h-6 items-center justify-between">
        <span className="h-5 w-16 animate-pulse rounded bg-butter-cream/70" />
        <span className="h-5 w-20 animate-pulse rounded bg-butter-cream/70" />
      </div>
    </div>
  );
}

function PaymentFormSkeleton() {
  return (
    <div className="flex flex-col gap-3" role="status" aria-label="Loading payment form">
      <div className="h-12 animate-pulse rounded-[9px] bg-butter-cream/70" />
      <div className="rounded-xl bg-white/60 p-4 sm:p-5">
        <div className="flex flex-col gap-3">
          <div className="h-12 animate-pulse rounded-[9px] bg-butter-cream/70" />
          <div className="flex gap-3">
            <div className="h-12 flex-1 animate-pulse rounded-[9px] bg-butter-cream/70" />
            <div className="h-12 flex-1 animate-pulse rounded-[9px] bg-butter-cream/70" />
          </div>
          <div className="h-12 animate-pulse rounded-[9px] bg-butter-cream/70" />
        </div>
      </div>

    </div>
  );
}
